import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { fittedModsDeep, type AttachKind, type Inventory, type WeaponSlotDef } from "@rietz/shared";
import { uiActions } from "../actions.js";
import type { OpenContainer } from "../store.js";
import { PANEL_LINE, TEXT, TEXT_DIM } from "../theme.js";
import { CELL, type DragState, type ItemSource } from "./drag.js";
import type { TileHandlers } from "./useInventoryDrag.js";
import { TileBody } from "./tile.js";
import { ModPreview, type ModAnchorPoints } from "./modPreview.js";
import {
  AREA_LABELS,
  SLOT_LABELS,
  compatibleParts,
  modIncompleteWarning,
  modSlotRows,
  modStatRows,
  type ModSlotRow,
  type OwnedWeapon,
} from "./modLogic.js";

/**
 * The Werkbank: the gun, its mounting points, what fits them, and what fitting
 * it would do — SEE it, CHANGE it, JUDGE it in three columns, because the
 * decision ("is this scope worth 340 € and a third of my ADS time?") needs all
 * three at once. DOM-only; every change is an ordinary `uiActions().tryOp`.
 *
 * Also the Inspect screen for every gun (`readOnly` drops the CHANGE column).
 * Surviving the weapon is `screen.tsx`'s job: it re-resolves `modWeaponIid`
 * every render and unmounts the pane when it fails, so this takes a RESOLVED
 * weapon.
 *
 * A gun is a tree of HOSTS. The assembly strip is that tree, depth-first with
 * a `└` on nested captions; every cell knows its host, so its op carries
 * `hostIid`. Rows are re-resolved from the live instance on every render, so a
 * detach collapses a whole block for free. The portrait's callouts stay one
 * level deep: `modAnchors` is keyed by `AttachKind` on the MODEL.
 */

/** The viewport the callout layout below is authored in. Exported: the
 *  fullscreen workbench CSS-scales exactly this box. */
export const PREVIEW_W = 640;
export const PREVIEW_H = 280;

export interface ModPaneProps {
  inv: Inventory;
  container: OpenContainer | null;
  /** Already resolved and known to be reachable — see the note above. */
  weapon: OwnedWeapon;
  /**
   * What to call the container a part is lying in. Defaults to "Container";
   * between raids it is "Stash".
   */
  containerLabel?: string;
  /** Shared inventory drag, used to light the exact slot under the part. */
  drag?: DragState | null;
  /** True for the private stash; false for a shared in-raid container. */
  containerOwned?: boolean;
  /** CSS scale applied by the floating dialog, used to keep WebGL sharp. */
  previewScale?: number;
  onTilePointerDown?: TileHandlers["onTilePointerDown"];
  /** Right-click on a fitted part: the inventory's own context menu (Inspect,
   *  and whatever else applies to a `mod` source). Absent on a read-only bench. */
  onTileContextMenu?: TileHandlers["onTileContextMenu"];
  /**
   * A bench you may look at but not work on — the shop counter's. Nothing that
   * would change the gun is drawn; everything that explains it stays, rows
   * still clickable for the callout.
   */
  readOnly?: boolean;
  /**
   * Right-click on a fitted part of a READ-ONLY bench. The shop puts up its own
   * menu and looks the part up itself — the gun is in nobody's kit.
   */
  onPartContextMenu?: (ev: React.MouseEvent, iid: string) => void;
}

export function ModPane(props: ModPaneProps): ReactElement {
  const { item, def } = props.weapon;

  /**
   * Which row's parts list is showing, by `ModSlotRow.key` (host iid + slot id
   * — two hosts can both declare a `tactical`).
   */
  const [selected, setSelected] = useState<string | null>(null);

  // A different gun means a different tree. Keyed on the iid, so re-fitting
  // parts on the SAME gun keeps the list open.
  useEffect(() => {
    setSelected(null);
  }, [item.iid]);

  const rows = modSlotRows(
    def,
    item,
    props.inv,
    props.containerOwned === true || props.weapon.area === "container" ? props.container : null,
  );
  // A row that has gone away simply deselects.
  const active = rows.find((r) => r.key === selected) ?? null;
  // The bench's half of the server's `"incomplete"` refusal. One line, above the
  // rows it is about, because a rifle in pieces is a fact about the whole gun.
  const warning = modIncompleteWarning(def, item);

  return (
    <div style={{ width: PREVIEW_W, display: "flex", flexDirection: "column", gap: 8 }}>
      <WeaponPreview
        weaponIid={item.iid}
        defId={def.id}
        // DEEP, so the model shows the gun the player actually built.
        mods={fittedModsDeep(def, item).map((f) => f.def.id)}
        rows={rows}
        drag={props.readOnly === true ? null : props.drag ?? null}
        displayScale={props.previewScale ?? 1}
        onTilePointerDown={props.readOnly === true ? undefined : props.onTilePointerDown}
        onTileContextMenu={props.readOnly === true ? undefined : props.onTileContextMenu}
        onPartContextMenu={props.readOnly === true ? props.onPartContextMenu : undefined}
        readOnly={props.readOnly === true}
        // Only a GUN-level row can light a callout — only the gun's own slots
        // have one.
        selected={active === null || active.depth !== 0 ? null : active.slot.id}
        onSelect={(slot) => {
          // The portrait only draws the GUN's own callouts, so a click on one
          // selects that gun-level row. `null` is the deselect gesture.
          setSelected(slot === null ? null : `${item.iid}:${slot}`);
        }}
      />

      <StatTable weapon={props.weapon} />

      {/* Nothing can be fitted on a read-only bench, so the list of what WOULD
          fit is not merely disabled, it is absent — the menu's own "absent
          beats disabled" rule, applied to the one section of this pane that
          would otherwise be an offer the shop cannot honour. */}
      {active === null || props.readOnly === true ? null : (
        <Section title={`AVAILABLE · ${SLOT_LABELS[active.slot.id].toUpperCase()}`}>
          <PartList
            inv={props.inv}
            container={props.container}
            containerLabel={props.containerLabel ?? AREA_LABELS.container}
            slot={active.slot}
            weaponIid={item.iid}
            hostIid={active.hostIid}
            occupied={active.fitted !== null}
          />
        </Section>
      )}

      {/* THE ASSEMBLY STRIP, LAST: one line of cells along the bottom, every
          mounting point on the gun in tree order, each a tile with a caption
          under it and nothing more. The names live in the tooltips. */}
      <Section title="ASSEMBLY">
        {warning === null ? null : <Warning>{warning}</Warning>}
        {rows.length === 0 ? (
          <Note>This weapon does not accept attachments.</Note>
        ) : (
          <div style={{ display: "flex", flexWrap: "nowrap", gap: 4, overflowX: "auto" }}>
            {rows.map((row) => (
              <SlotCell
                key={row.key}
                row={row}
                weaponIid={item.iid}
                drag={props.readOnly === true ? null : props.drag ?? null}
                onTilePointerDown={props.readOnly === true ? undefined : props.onTilePointerDown}
                onTileContextMenu={props.readOnly === true ? undefined : props.onTileContextMenu}
                onPartContextMenu={props.readOnly === true ? props.onPartContextMenu : undefined}
                readOnly={props.readOnly === true}
                selected={row.key === selected}
                onSelect={() => setSelected(row.key === selected ? null : row.key)}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weapon inspection
// ---------------------------------------------------------------------------

/**
 * The interactive weapon model. The renderer lives in a ref and outlives
 * re-renders; `mods` is joined into the dependency so a fresh array with the
 * same contents does not rebuild it.
 */
interface CalloutLayout {
  left: number;
  top: number;
  lineX: number;
  lineY: number;
}

/**
 * Where each kind's callout box sits beside the portrait: a clock face around
 * the gun, left to right in the order the parts are in real life (the camera
 * looks down the barrel from +x, muzzle to the right). Boxes are 52×44 in a
 * 640×280 viewport, placed so none overlap or run off the bottom. `lineX`/
 * `lineY` is where the leader line STARTS; the far end follows the 3-D anchor
 * (`moveLines`). One entry per kind — only the gun's own slots get callouts.
 */
const CALLOUT_LAYOUT: Record<AttachKind, CalloutLayout> = {
  // Over the receiver, left to right along the top.
  receiver: { left: 200, top: 10, lineX: 226, lineY: 54 },
  optic: { left: 286, top: 10, lineX: 312, lineY: 54 },
  barrel: { left: 430, top: 10, lineX: 456, lineY: 54 },
  // The two ends.
  stock: { left: 14, top: 160, lineX: 66, lineY: 182 },
  muzzle: { left: 566, top: 96, lineX: 566, lineY: 118 },
  // Mid-height, forward: the fore-end the support hand is on.
  handguard: { left: 400, top: 150, lineX: 426, lineY: 150 },
  // Underneath, left to right: firing hand, magazine, support-hand grip, and
  // whatever is clamped to the rail in front of it.
  pistolgrip: { left: 150, top: 224, lineX: 176, lineY: 224 },
  mag: { left: 240, top: 224, lineX: 266, lineY: 224 },
  grip: { left: 350, top: 224, lineX: 376, lineY: 224 },
  tactical: { left: 440, top: 224, lineX: 466, lineY: 224 },
};

export function WeaponPreview(props: {
  weaponIid: string;
  defId: string;
  mods: readonly string[];
  rows: readonly ModSlotRow[];
  drag: DragState | null;
  displayScale: number;
  onTilePointerDown?: TileHandlers["onTilePointerDown"];
  onTileContextMenu?: TileHandlers["onTileContextMenu"];
  /** See `ModPaneProps.onPartContextMenu`. Only ever set on a read-only bench. */
  onPartContextMenu?: (ev: React.MouseEvent, iid: string) => void;
  /** Look, don't touch — see `ModPaneProps.readOnly`. */
  readOnly: boolean;
  selected: string | null;
  onSelect: (slot: string | null) => void;
}): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<ModPreview | null>(null);
  const orbitPointer = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [orbiting, setOrbiting] = useState(false);
  const modsKey = props.mods.join(",");
  // Only the gun's own slots get a callout; nested rows live in the assembly
  // list.
  const callouts = props.rows.filter((row) => row.depth === 0);

  const moveLines = useCallback((points: ModAnchorPoints): void => {
    const host = hostRef.current;
    if (host === null) return;
    for (const [kind, point] of Object.entries(points)) {
      if (point === undefined) continue;
      const line = host.querySelector<SVGLineElement>(`[data-mod-line="${kind}"]`);
      const dot = host.querySelector<SVGCircleElement>(`[data-mod-dot="${kind}"]`);
      line?.setAttribute("x2", point.x.toFixed(1));
      line?.setAttribute("y2", point.y.toFixed(1));
      dot?.setAttribute("cx", point.x.toFixed(1));
      dot?.setAttribute("cy", point.y.toFixed(1));
      const visibility = point.visible ? "visible" : "hidden";
      line?.setAttribute("visibility", visibility);
      dot?.setAttribute("visibility", visibility);
    }
  }, []);

  // The canvas is NOT in this JSX: `ModPreview` owns its own (see
  // `modPreview.ts`).
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const made = new ModPreview(host, PREVIEW_W, PREVIEW_H, moveLines);
    previewRef.current = made;
    made.start();
    return () => {
      previewRef.current = null;
      // Renderer, GL context, canvas, materials and lights, all of it.
      made.dispose();
    };
  }, [moveLines]);

  // Runs after the mount effect above on the first render, so the preview is
  // always there to be told what to draw.
  useEffect(() => {
    previewRef.current?.setWeapon(props.defId, modsKey === "" ? [] : modsKey.split(","));
  }, [props.defId, modsKey]);

  // Match the renderer's backing resolution to the dialog's CSS-scaled size, so
  // enlarging reveals pixels instead of stretching.
  useEffect(() => {
    previewRef.current?.setDisplayScale(props.displayScale);
  }, [props.displayScale]);

  const endOrbit = (pointerId: number): void => {
    if (orbitPointer.current?.pointerId !== pointerId) return;
    orbitPointer.current = null;
    setOrbiting(false);
  };

  return (
    <div
      ref={hostRef}
      data-weapon-iid={props.weaponIid}
      onPointerDown={(ev) => {
        if (ev.button !== 0 || (ev.target as Element).closest("[data-weapon-slot]") !== null) {
          return;
        }
        ev.preventDefault();
        ev.stopPropagation();
        ev.currentTarget.setPointerCapture(ev.pointerId);
        orbitPointer.current = { pointerId: ev.pointerId, x: ev.clientX, y: ev.clientY };
        setOrbiting(true);
      }}
      onPointerMove={(ev) => {
        const active = orbitPointer.current;
        if (active === null || active.pointerId !== ev.pointerId) return;
        const dx = ev.clientX - active.x;
        const dy = ev.clientY - active.y;
        active.x = ev.clientX;
        active.y = ev.clientY;
        previewRef.current?.rotateBy(dx, dy);
      }}
      onPointerUp={(ev) => endOrbit(ev.pointerId)}
      onPointerCancel={(ev) => endOrbit(ev.pointerId)}
      onLostPointerCapture={(ev) => endOrbit(ev.pointerId)}
      style={{
        position: "relative",
        width: PREVIEW_W,
        height: PREVIEW_H,
        borderRadius: 4,
        border: `1px solid ${PANEL_LINE}`,
        background:
          "repeating-linear-gradient(135deg, rgba(255,255,255,.015) 0 3px, transparent 3px 9px), #080a0b",
        overflow: "hidden",
        cursor: orbiting ? "grabbing" : "grab",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <svg
        width={PREVIEW_W}
        height={PREVIEW_H}
        viewBox={`0 0 ${PREVIEW_W} ${PREVIEW_H}`}
        style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}
      >
        {callouts.map((row) => {
          const layout = CALLOUT_LAYOUT[row.slot.id];
          const active = props.selected === row.slot.id;
          return (
            <g key={row.slot.id}>
              <line
                data-mod-line={row.slot.id}
                x1={layout.lineX}
                y1={layout.lineY}
                x2={PREVIEW_W / 2}
                y2={PREVIEW_H / 2}
                stroke={active ? "rgba(217, 236, 250, 0.9)" : "rgba(148, 174, 194, 0.72)"}
                strokeWidth={active ? 1.5 : 1}
              />
              <circle
                data-mod-dot={row.slot.id}
                cx={PREVIEW_W / 2}
                cy={PREVIEW_H / 2}
                r={active ? 4 : 3}
                fill={row.fitted === null ? "#8fa4b5" : "#9ed3a5"}
              />
            </g>
          );
        })}
      </svg>

      {callouts.map((row) => {
        const layout = CALLOUT_LAYOUT[row.slot.id];
        const active = props.selected === row.slot.id;
        const hover = slotDropHover(props.drag, props.weaponIid, row.slot.id, row.hostIid);
        const draggingThis = isDraggingRow(props.drag, props.weaponIid, row.slot.id, row.hostIid);
        return (
          <div
            key={row.key}
            role="button"
            tabIndex={0}
            data-weapon-iid={props.weaponIid}
            data-weapon-slot={row.slot.id}
            data-weapon-host={row.hostIid}
            className={`modcallout${active ? " modcallout-active" : ""}${
              hover === null ? "" : hover.ok ? " moddrop-valid" : " moddrop-invalid"
            }${draggingThis ? " modsource-dragging" : ""}`}
            style={{ left: layout.left, top: layout.top }}
            onClick={() => props.onSelect(active ? null : row.slot.id)}
            onPointerDown={
              row.fitted === null || props.onTilePointerDown === undefined
                ? undefined
                : (ev) => {
                    props.onTilePointerDown?.(
                      ev,
                      { item: row.fitted!.item, x: 0, y: 0, rot: 0 },
                      { t: "mod", weaponIid: props.weaponIid, slot: row.slot.id },
                    );
                  }
            }
            onContextMenu={
              row.fitted === null
                ? undefined
                : props.onTileContextMenu !== undefined
                  ? (ev) => {
                      props.onTileContextMenu?.(
                        ev,
                        { item: row.fitted!.item, x: 0, y: 0, rot: 0 },
                        { t: "mod", weaponIid: props.weaponIid, slot: row.slot.id },
                      );
                    }
                  : props.onPartContextMenu !== undefined
                    ? (ev) => {
                        props.onPartContextMenu?.(ev, row.fitted!.item.iid);
                      }
                    : undefined
            }
            onKeyDown={(ev) => {
              if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                props.onSelect(active ? null : row.slot.id);
              }
            }}
            title={`${SLOT_LABELS[row.slot.id]} · ${row.fitted?.def.name ?? "Empty"}${
              row.load === null ? "" : ` ·${loadSuffix(row)} rounds`
            }`}
          >
            {row.fitted === null ? (
              <span className="modslot-empty" aria-label="Empty slot">+</span>
            ) : (
              <TileBody
                def={row.fitted.def}
                item={row.fitted.item}
                iconOnly
                cursor={props.readOnly ? "default" : "grab"}
              />
            )}
          </div>
        );
      })}

    </div>
  );
}

// ---------------------------------------------------------------------------
// The numbers
// ---------------------------------------------------------------------------

/** Better / worse / unchanged, as three colours and nothing else. */
const BETTER = "#7ec98a";
const WORSE = "#d8776c";

function StatTable(props: { weapon: OwnedWeapon }): ReactElement {
  const rows = modStatRows(props.weapon.def, props.weapon.item);

  return (
    <Section title="WEAPON CHARACTERISTICS">
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {rows.map((row) => (
          <div
            key={row.key}
            style={{
              display: "grid",
              gridTemplateColumns: "118px 1fr 72px",
              gap: 7,
              alignItems: "center",
              fontSize: 10,
            }}
          >
            <span style={{ color: TEXT_DIM, letterSpacing: "0.04em" }}>{row.label}</span>
            <span className="modstat-track">
              <span
                className="modstat-fill"
                style={{
                  width: `${statPercent(row.key, row.current)}%`,
                  background:
                    row.verdict === 1
                      ? "rgba(116, 161, 177, 0.72)"
                      : row.verdict === -1
                        ? "rgba(121, 70, 69, 0.72)"
                        : "rgba(166, 176, 181, 0.66)",
                }}
              />
            </span>
            <span
              style={{
                textAlign: "right",
                color: row.verdict === 1 ? BETTER : row.verdict === -1 ? WORSE : TEXT,
              }}
            >
              {row.currentText}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function statPercent(key: string, current: number): number {
  const maxima: Record<string, number> = {
    rpm: 900,
    mag: 60,
    reload: 5,
    ads: 0.65,
    spread: 6,
    v0: 900,
    zoom: 6,
    recoil: 1.2,
  };
  const max = maxima[key] ?? Math.max(1, current);
  return Math.max(3, Math.min(100, (current / max) * 100));
}

// ---------------------------------------------------------------------------
// Slots and parts
// ---------------------------------------------------------------------------

/**
 * Which drop the bench should light on one row. A `weapon` hover lights exactly
 * its mounting point, HOST AND ALL; a `load` hover names no slot and lights the
 * magazine well.
 */
function slotDropHover(
  drag: DragState | null | undefined,
  weaponIid: string,
  slot: AttachKind,
  hostIid: string,
): { ok: boolean } | null {
  const h = drag?.hover ?? null;
  if (h === null) return null;
  if (h.t === "weapon") {
    // Absent means the gun, on the hover exactly as on the op.
    const host = h.hostIid ?? weaponIid;
    return h.weaponIid === weaponIid && h.slot === slot && host === hostIid ? h : null;
  }
  if (h.t === "load") {
    return h.ontoIid === weaponIid && slot === "mag" && hostIid === weaponIid ? h : null;
  }
  return null;
}

/** True while THIS row's part is the thing being dragged. Same host arithmetic
 *  as the hover above: a bare `mod` source is a part off the gun itself. */
function isDraggingRow(
  drag: DragState | null | undefined,
  weaponIid: string,
  slot: AttachKind,
  hostIid: string,
): boolean {
  const from = drag?.from ?? null;
  if (from === null || from.t !== "mod") return false;
  return (
    from.weaponIid === weaponIid &&
    from.slot === slot &&
    (from.hostIid ?? weaponIid) === hostIid
  );
}

/** "17/30" for a fitted magazine, or the empty string for every other row. */
function loadSuffix(row: ModSlotRow): string {
  return row.load === null ? "" : ` ${row.load.rounds}/${row.load.capacity}`;
}

/** The cell: the inventory's own tile size, so a part looks the same on the
 *  bench as it does in a pocket. */
const SLOT_CELL = 46;

/**
 * ONE CELL OF THE ASSEMBLY STRIP: a mounting point, what is in it, a caption
 * (red when a vital slot is empty, `└` when nested). Carries `data-weapon-host`
 * beside `data-weapon-slot` so a drop is judged against this host's whitelist —
 * see `weaponTargetAt`.
 */
function SlotCell(props: {
  row: ModSlotRow;
  weaponIid: string;
  drag: DragState | null;
  onTilePointerDown?: TileHandlers["onTilePointerDown"];
  onTileContextMenu?: TileHandlers["onTileContextMenu"];
  /** See `ModPaneProps.onPartContextMenu`. Only ever set on a read-only bench. */
  onPartContextMenu?: (ev: React.MouseEvent, iid: string) => void;
  /** Look, don't touch — see `ModPaneProps.readOnly`. */
  readOnly: boolean;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  const { row } = props;
  const fitted = row.fitted;
  // The gun's own cells send no `hostIid`; a nested cell names its host.
  const source: ItemSource =
    row.hostIid === props.weaponIid
      ? { t: "mod", weaponIid: props.weaponIid, slot: row.slot.id }
      : { t: "mod", weaponIid: props.weaponIid, slot: row.slot.id, hostIid: row.hostIid };
  const hover = slotDropHover(props.drag, props.weaponIid, row.slot.id, row.hostIid);
  const draggingThis = isDraggingRow(props.drag, props.weaponIid, row.slot.id, row.hostIid);
  // An empty VITAL slot is the one cell that is not an upgrade: the gun does
  // not fire, and the caption says so in red.
  const missingVital = row.vital && fitted === null;
  const caption = `${row.depth === 0 ? "" : "└ "}${SLOT_LABELS[row.slot.id]}${loadSuffix(row)}`;

  return (
    <div
      role="button"
      tabIndex={0}
      data-weapon-iid={props.weaponIid}
      data-weapon-slot={row.slot.id}
      data-weapon-host={row.hostIid}
      className={`modslot${hover === null ? "" : hover.ok ? " moddrop-valid" : " moddrop-invalid"}${
        draggingThis ? " modsource-dragging" : ""
      }`}
      style={{
        flex: "0 0 auto",
        alignItems: "center",
        width: SLOT_CELL + 6,
        padding: 2,
        boxSizing: "border-box",
        border: `1px solid ${
          props.selected
            ? "rgba(156, 181, 197, 0.72)"
            : missingVital
              ? "rgba(190, 118, 108, 0.62)"
              : "rgba(118, 132, 141, 0.42)"
        }`,
        background: props.selected ? "rgba(67, 85, 96, 0.72)" : "rgba(18, 22, 24, 0.88)",
      }}
      onClick={props.onSelect}
      onPointerDown={
        fitted === null || props.onTilePointerDown === undefined
          ? undefined
          : (ev) => {
              props.onTilePointerDown?.(ev, { item: fitted.item, x: 0, y: 0, rot: 0 }, source);
            }
      }
      onContextMenu={
        fitted === null
          ? undefined
          : props.onTileContextMenu !== undefined
            ? (ev) => {
                props.onTileContextMenu?.(ev, { item: fitted.item, x: 0, y: 0, rot: 0 }, source);
              }
            : props.onPartContextMenu !== undefined
              ? (ev) => {
                  props.onPartContextMenu?.(ev, fitted.item.iid);
                }
              : undefined
      }
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          props.onSelect();
        }
      }}
      title={`${SLOT_LABELS[row.slot.id]}${row.vital ? " (vital)" : ""} · ${
        fitted !== null
          ? fitted.def.name
          : missingVital
            ? "Missing — the weapon will not fire"
            : "Empty"
      }${loadSuffix(row) === "" ? "" : ` ·${loadSuffix(row)} rounds`}`}
    >
      <span className="modslot-item" style={{ flex: "0 0 auto", width: SLOT_CELL, height: SLOT_CELL }}>
        {fitted === null ? (
          <span className="modslot-empty" aria-label="Empty slot">+</span>
        ) : (
          <TileBody
            def={fitted.def}
            item={fitted.item}
            iconOnly
            cursor={props.readOnly ? "default" : "grab"}
          />
        )}
      </span>
      <span
        className="modslot-label"
        style={{
          color: missingVital ? WORSE : undefined,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
      >
        {caption}
      </span>
    </div>
  );
}

function PartList(props: {
  inv: Inventory;
  container: OpenContainer | null;
  containerLabel: string;
  slot: WeaponSlotDef;
  weaponIid: string;
  /** Which host inside the gun the slot belongs to — the gun's own iid for a
   *  depth-0 row. Goes on the op; see `ModSlotRow.hostIid`. */
  hostIid: string;
  /** A filled slot has to be emptied first — `attach` refuses "slot-occupied". */
  occupied: boolean;
}): ReactElement {
  const parts = compatibleParts(props.inv, props.container, props.slot);

  if (props.occupied) {
    return <Note>Drag the installed part into your inventory first.</Note>;
  }
  if (parts.length === 0) {
    return <Note>No compatible parts available.</Note>;
  }

  return (
    <div className="modparts">
      {parts.map((part) => (
        <button
          key={part.item.iid}
          type="button"
          className="modpart"
          style={{ width: part.def.w * CELL, height: part.def.h * CELL }}
          aria-label={`${part.def.name}, ${
            part.area === "container" ? props.containerLabel : AREA_LABELS[part.area]
          }`}
          title={`${part.def.name} · ${
            part.area === "container" ? props.containerLabel : AREA_LABELS[part.area]
          }${part.def.description === undefined ? "" : ` — ${part.def.description}`}`}
          onClick={() => {
            uiActions().tryOp(
              // Absent `hostIid` IS the gun on the wire, so a gun-level fit
              // sends exactly the payload it sent before protocol v18.
              props.hostIid === props.weaponIid
                ? {
                    t: "attach",
                    iid: part.item.iid,
                    weaponIid: props.weaponIid,
                    slot: props.slot.id,
                  }
                : {
                    t: "attach",
                    iid: part.item.iid,
                    weaponIid: props.weaponIid,
                    slot: props.slot.id,
                    hostIid: props.hostIid,
                  },
            );
          }}
        >
          <TileBody def={part.def} item={part.item} iconOnly cursor="pointer" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section(props: { title?: string; children: ReactNode }): ReactElement {
  return (
    <section
      style={{
        padding: "7px 8px",
        borderRadius: 2,
        border: `1px solid ${PANEL_LINE}`,
        background: "rgba(8, 10, 11, 0.88)",
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      {props.title === undefined ? null : (
        <span style={{ fontSize: 9, letterSpacing: "0.1em", color: TEXT_DIM }}>
          {props.title}
        </span>
      )}
      {props.children}
    </section>
  );
}

function Note(props: { children: ReactNode }): ReactElement {
  return <span style={{ fontSize: 10, color: TEXT_DIM }}>{props.children}</span>;
}

/**
 * The rifle is in pieces, said once above the rows. Loud on purpose — the one
 * thing here that is not a matter of degree. `role="status"`: a standing
 * condition, not an event.
 */
function Warning(props: { children: ReactNode }): ReactElement {
  return (
    <div
      role="status"
      style={{
        fontSize: 11,
        color: WORSE,
        padding: "4px 6px",
        borderRadius: 2,
        border: "1px solid rgba(190, 118, 108, 0.55)",
        background: "rgba(58, 26, 24, 0.55)",
      }}
    >
      {props.children}
    </div>
  );
}
